import { StoreApi, UseBoundStore } from 'zustand'

import { STORE_STATUS } from '@/constants/store'
import { WebSocketService } from '@/service/websocketService'
import logger from '@/utils/_legacy/logger'

import { createBaseTableStore } from './table.store'
import type {
  CreateWebSocketTableStoreConfig,
  FullStoreActions,
  FullStoreState,
  WebSocketActions,
  WebSocketState
} from './table.types'

export function createWebSocketTableStore<
  TData,
  TRawData,
  TConnectParams,
  TSortAttribute extends string = string,
  TFilterParamName extends string = string
>(
  config: CreateWebSocketTableStoreConfig<
    TData,
    TRawData,
    TConnectParams,
    TSortAttribute,
    TFilterParamName
  >
): UseBoundStore<
  StoreApi<
    FullStoreState<TData, TSortAttribute, TFilterParamName, WebSocketState, TConnectParams> &
      FullStoreActions<
        TSortAttribute,
        TFilterParamName,
        TConnectParams,
        WebSocketActions<TConnectParams>
      >
  >
> {
  const { websocketConfig, ...baseConfig } = config

  let wsService: WebSocketService<TRawData> | null = null
  let currentConnectParams: TConnectParams | null = null

  return createBaseTableStore<
    TData,
    TSortAttribute,
    TFilterParamName,
    TConnectParams,
    WebSocketState,
    WebSocketActions<TConnectParams>
  >({
    ...baseConfig,
    fetchApi: async () => ({
      content: [],
      totalElements: 0,
      isConnected: false,
      isConnecting: false,
      wsError: null
    }),
    customInitialState: {
      isConnected: false,
      isConnecting: false,
      wsError: null
    },
    customActions: (set, get) => ({
      connectWebSocket: (params: TConnectParams) => {
        const { shouldReuseConnection } = websocketConfig
        const shouldReuse = shouldReuseConnection?.(currentConnectParams, params) ?? false

        if (shouldReuse && get().isConnecting) return

        if (wsService?.isConnected() && shouldReuse && get().data.length > 0) return

        if (wsService && (!shouldReuse || get().data.length === 0)) {
          wsService.disconnect()
          wsService = null
          if (!shouldReuse) {
            set({ data: [], error: null, wsError: null })
          }
        }

        const authToken = websocketConfig.getAuthToken?.()

        if (websocketConfig.getAuthToken && !authToken) {
          set({
            wsError: 'Authentication token not available',
            isConnecting: false,
            isConnected: false
          })
          return
        }

        wsService = new WebSocketService<TRawData>({
          url: websocketConfig.getUrl(params),
          heartbeatInterval: websocketConfig.heartbeatInterval ?? 30000
        })

        currentConnectParams = params

        set({
          isConnected: false,
          isConnecting: true,
          wsError: null,
          error: null,
          status: STORE_STATUS.idle
        })

        wsService.onMessage((rawData: TRawData) => {
          try {
            const transformedData = websocketConfig.transformData(rawData)
            const currentData = get().data

            const newData =
              Array.isArray(currentData) && Array.isArray(transformedData)
                ? [...transformedData, ...currentData]
                : transformedData

            set({
              data: newData,
              status: STORE_STATUS.idle,
              isConnected: true,
              isConnecting: newData.length > 0 ? false : get().isConnecting,
              wsError: null,
              error: null
            })
          } catch (error) {
            logger.error(error, 'Failed to process WebSocket data')
            set({
              wsError: `Failed to process data (${error instanceof Error ? error.message : 'Unknown error'})`,
              isConnecting: false,
              isConnected: false
            })
          }
        })

        wsService.onOpen(() => {
          set({ isConnected: true, wsError: null, error: null })
          if (authToken) {
            wsService?.send(JSON.stringify({ authToken }))
          }
        })

        wsService.onClose(() => {
          set({ isConnected: false, isConnecting: false })
        })

        wsService.onError(() => {
          set({
            data: [],
            wsError: 'WebSocket connection error',
            isConnected: false,
            isConnecting: false
          })
        })

        wsService.connect()
      },

      disconnectWebSocket: () => {
        wsService?.disconnect()
        wsService = null
        currentConnectParams = null
        set({ isConnected: false, isConnecting: false })
      }
    })
  })
}
